import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import Link from "next/link";
import PetsIcon from "../icons/pet";
import AppointmentsIcon from "../icons/appointment";
import PhoneIcon from "../icons/phone";
import PersonIcon from "../icons/person";
import LogoutIcon from "../icons/logout";
import MenuIcon from "../icons/menu";
import CreditCard from "../icons/credit-card";
import CloseIcon from "../icons/close";
import BusinessIcon from "../icons/storefront";
import Logo from "../icons/logo";
// import Notifications from "../Notification";
import LogoMobile from "../icons/logomobile";
import Profile from "../Profile";
import ActionItem from "../ActionItem";
import GlobalSide from "../GlobalSide";
import useOutsideClickHider from "../helpers/OutsideClickHider";
import GlobalFooter from "../GlobalFooter";
import useCustomerUser from "../../hooks/use-customer-user";

const IconWrapper = styled.div`
  cursor: pointer;
`;

const MenuWrapper = styled.div`
  padding: 9px 0 9px 9px;
  ${({ theme }) => theme.mq.lg`
    display: none;
  `}
`;

const Menu = React.forwardRef((props, ref) => {
  const { setHideSide, hideSide } = props;

  return (
    <MenuWrapper>
      <IconWrapper
        ref={ref}
        onClick={() => {
          setHideSide(!hideSide);
        }}
      >
        {hideSide ? <MenuIcon color="#27272F" /> : <CloseIcon />}
      </IconWrapper>
    </MenuWrapper>
  );
});

Menu.propTypes = {
  hideSide: PropTypes.bool,
  setHideSide: PropTypes.func,
};

Menu.defaultProps = {
  hideSide: true,
  setHideSide: {},
};

const actionItems = [
  {
    id: 1,
    Icon: AppointmentsIcon,
    text: "My Appointments",
    redirectUrl: "/customer/appointments",
  },
  {
    id: 2,
    Icon: PetsIcon,
    text: "My Pets",
    redirectUrl: "/customer/pets",
  },
];

// const MockNotificationsListConsumer = [
//   {
//     id: 1,
//     title: "Rate your visit to “The Rose",
//     timeSent: "3hr",
//     personName: "Rebecca Newton",
//   },
//   {
//     id: 2,
//     title: "Notification Message",
//     timeSent: "10/12/21",
//     personName: "Sandra Norrige",
//   },
//   {
//     id: 3,
//     title: "See you soon!",
//     timeSent: "10/13/21",
//     personName: "Boon Flewkins",
//   },
//   {
//     id: 4,
//     title: 'Rate your visit to "Meow Friend"',
//     timeSent: "10/14/21",
//     personName: "Kevin Malcolm Jr.",
//   },
//   {
//     id: 5,
//     title: "New Review Submitted",
//     timeSent: "10/15/21",
//     personName: "Carson Michael",
//   },
// ];

const profileItems = [
  {
    id: 1,
    Icon: PersonIcon,
    text: "MY PROFILE",
    redirectUrl: "/customer/user-profile",
    userType: "customer",
  },
  {
    id: 2,
    Icon: CreditCard,
    text: "PAYMENT SETTINGS",
    redirectUrl: "/customer/payment-settings",
    userType: "customer",
  },
  {
    id: 3,
    Icon: PhoneIcon,
    text: "CONTACT SUPPORT",
    redirectUrl: "mailto:support@goodfriend.com",
    userType: "customer",
  },
  {
    id: 4,
    Icon: BusinessIcon,
    text: "List Your Business",
    // redirectUrl: "/customer/user-profile",
    userType: "customer",
    isList: true,
  },
  {
    id: 5,
    Icon: LogoutIcon,
    text: "Log Out",
    redirectUrl: "/customer/auth",
    isLogout: true,
    userType: "customer",
  },
];

const footerSupportItem = {
  id: 1,
  Icon: PhoneIcon,
  text: "Contact Support",
  redirectUrl: "mailto:support@goodfriend.com",
};

const GridWrapper = styled.div`
  ${({ theme }) => theme.mq.xsOnly`
    grid-template-rows: 70px auto fit-content(300px);
  `}

  display: grid;
  min-height: 100vh;
  grid-template-rows: 70px auto fit-content(250px);
  grid-template-areas:
    "header"
    "main"
    "footer";
`;

const Header = styled.nav`
  background-color: ${({ theme }) => theme.themeColors.white};
  box-shadow: ${({ theme }) => theme.settings.shadows.boxShadow};
  display: flex;
  grid-area: header;
  height: 70px;
  justify-content: space-between;
  width: 100%;
  z-index: 5;
`;

const HeaderButtons = styled.div`
  margin: auto 25px auto 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ theme }) => theme.mq.lg`
    margin: auto 30px auto 0;
  `};
`;

const Main = styled.div`
  grid-area: main;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
`;

const HeaderActionItemWrapper = styled.div`
  display: none;

  ${({ theme }) => theme.mq.lg`
    display: block;
    margin: 20px 45px 8px 0;
  `}
`;

const LogoWrapperLg = styled.div`
  display: none;

  ${({ theme }) => theme.mq.lg`
    margin: auto 0 auto 30px;
    display: block;
  `};
`;

const LogoWrapperMd = styled.div`
  display: block;
  margin: auto 25px;

  ${({ theme }) => theme.mq.lg`
    display: none;
  `};
`;

const Overlay = styled.div`
  display: none;
  opacity: 0;
  overflow: hidden;

  ${({ isSideNavVisible }) =>
    !isSideNavVisible &&
    css`
      background-color: #5a5b64;
      display: block;
      height: 100%;
      left: 0;
      opacity: 0.5;
      position: fixed;
      top: 0;
      width: 100%;
      z-index: 2;
    `}
`;

const CustomerNavigation = (props) => {
  const [hideSide, setHideSide] = useState(true);
  const sideNavRef = React.useRef(null);
  const IconEl = React.useRef(null);
  useOutsideClickHider([sideNavRef, IconEl], setHideSide);
  const { children } = props;
  const customerUser = useCustomerUser();

  return (
    <GridWrapper hideSide={hideSide}>
      <Header aria-label="primary">
        <LogoWrapperLg>
          <Link href="/">
            <a>
              <Logo color="#27272f" />
            </a>
          </Link>
        </LogoWrapperLg>
        <LogoWrapperMd>
          <Link href="/">
            <a>
              <LogoMobile color="#27272f" />
            </a>
          </Link>
        </LogoWrapperMd>
        <HeaderButtons>
          {actionItems.map((item) => {
            return (
              <HeaderActionItemWrapper key={item.id}>
                <ActionItem fontSize="14px" {...item} />
              </HeaderActionItemWrapper>
            );
          })}
          {/* COMMENTING OUT FOR POST MVP USE */}
          {/* <Notifications
            mockNotificationsList={MockNotificationsListConsumer}
            iconColor="#27272f"
          /> */}
          <Profile
            isCustomerNav
            profileCardIdentity={customerUser}
            profileItems={profileItems}
          />
          <Menu ref={IconEl} setHideSide={setHideSide} hideSide={hideSide} />
        </HeaderButtons>
      </Header>
      <Main initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <>
          <Overlay isSideNavVisible={hideSide} />
          {children}
        </>
      </Main>
      <GlobalSide
        hideLg
        profileCardIdentity={customerUser}
        footerSupportItem={footerSupportItem}
        profileItems={profileItems}
        sideBarItems={actionItems}
        ref={sideNavRef}
        hideSide={hideSide}
        isCustomerNav
      />
      <GlobalFooter />
    </GridWrapper>
  );
};

export default CustomerNavigation;

CustomerNavigation.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
